/**
 * "Schedule an event" (place manager, unit 6b): one form, inline in the
 * Schedule section — a template of this place (optional), the game, when and
 * for how long, the season, the rules, the title — then "Review", one plain
 * line, then "Create". The same form changes an event coming up (unit 6c,
 * `change-event.ts`): filled in from the event as read, no template, the
 * review line naming only what changes, then "Apply". Cancelling one shows
 * its own review line, then "Cancel it". Pure presentation over
 * `schedule-event.ts` and `change-event.ts` (the page's `useScheduleEvent`
 * makes the calls), so the proof renders it from fixtures.
 */
import * as React from "react";

import { fromLocalInput, localInputValue } from "@/lib/dates";
import type { PlaceData } from "@/data/places-api";
import type { PlaceSeasonsData } from "@/data/place-events-api";
import type { PlaceGames } from "@/features/places/games";
import type { StaffGroup, TemplateRow } from "@/features/places/schedule";
import {
  buildCreateEventInput,
  defaultTitle,
  draftContextOf,
  draftDefaults,
  draftProblems,
  nameOf,
  reviewLine,
  seasonAt,
  withTemplate,
  LostAnswerError,
  type ScheduleDraft,
  type ScheduleOptions,
  type TemplateField,
} from "@/features/places/schedule-event";
import {
  TOO_CLOSE_LINE,
  buildUpdateEventInput,
  cancelReviewLine,
  changeOptionsOf,
  changeProblems,
  changeReviewLine,
  changeSeasonsOf,
  draftOfEvent,
  rowWriteOf,
  snapshotProblem,
  unreadPresetsOf,
  type EventSnapshot,
} from "@/features/places/change-event";
import type { Scheduling } from "@/features/places/use-schedule-event";
import type { Load } from "@/features/places/use-places";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";

/** Where the form stands: being filled in, its review line shown, sending, or
 *  sent with its answer lost (no Create again from this form). */
export type FormStep = "edit" | "review" | "creating" | "lost";

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

/** What the template choice says while the templates are not to hand. */
function templatesNote(templates: StaffGroup<TemplateRow>): string | null {
  if (templates.state === "unread" || templates.state === "reading") return "Reading this place's templates…";
  if (templates.state === "failed") return "This place's templates did not read, so none can be picked.";
  if (templates.state === "ready" && templates.rows.length === 0) return "No templates tied to this place.";
  return null;
}

/** A picker's option label: its name, said as not offered for new events when
 *  it is the event's own and not among the form's (a game switched off, an
 *  archived preset). */
function optionLabel(o: { slug: string; name: string }, offered: readonly { slug: string }[]): string {
  return offered.some((x) => x.slug === o.slug) ? o.name : `${o.name} (not offered for new events)`;
}

/** Changing an event: the event as read, and the change to send. */
export interface ChangeMode {
  event: EventSnapshot;
  onApply: Scheduling["change"];
}

/** The form once its games and presets are read. `change`: it changes that
 *  event instead of making one. `now` and `step` are for the proof (a fixed
 *  clock, the review shown). */
function DraftForm({
  place,
  options: offered,
  games,
  seasons,
  templates,
  username,
  onCreate,
  change,
  onClose,
  now: fixedNow,
  step: initialStep = "edit",
}: {
  place: PlaceData;
  options: ScheduleOptions;
  games: PlaceGames | null;
  seasons: PlaceSeasonsData | null;
  templates: StaffGroup<TemplateRow>;
  username: string;
  onCreate: Scheduling["create"];
  change?: ChangeMode;
  onClose: () => void;
  now?: number;
  step?: FormStep;
}) {
  const now = fixedNow ?? Date.now();
  const event = change?.event ?? null;
  const ctx = draftContextOf({ now, place, games, seasons, options: offered });
  // Changing: the event's own game, presets and season are offered too.
  const options = event ? changeOptionsOf(offered, event) : offered;
  const seasonList = event ? changeSeasonsOf(ctx.seasons, event) : ctx.seasons;
  const [draft, setDraft] = React.useState<ScheduleDraft>(() => (event ? draftOfEvent(event) : draftDefaults(ctx)));
  // The title follows the game until it is typed in (or a template names one);
  // the season follows the start until one is picked (or a template names one).
  // Changing an event, neither follows: only what is changed changes.
  const [titleTyped, setTitleTyped] = React.useState(event !== null);
  const [seasonPicked, setSeasonPicked] = React.useState(event !== null);
  // The fields the current template set and the user has not changed since.
  const [templateSet, setTemplateSet] = React.useState<TemplateField[]>([]);
  const [step, setStep] = React.useState<FormStep>(initialStep);
  const [failure, setFailure] = React.useState<string | null>(null);

  const rows = templates.state === "ready" && !event ? templates.rows : [];
  const template = rows.find((t) => t.id === draft.templateId) ?? null;
  // Only what this mode sends is built: the create, or the change.
  const input = event ? null : buildCreateEventInput(draft, { placeId: place.id, username, now, template });
  const update = event ? buildUpdateEventInput(event, draft, now) : null;
  const problems = event ? changeProblems(event, draft, now, seasonList) : draftProblems(draft, now, ctx.seasons);
  const note = templatesNote(templates);
  // A preset the event does not read back: shown as not read, its picker held.
  const unread = event ? unreadPresetsOf(event) : new Set<string>();

  /** An edit by the user: the fields it touches are theirs from now on. */
  const edit = (patch: Partial<ScheduleDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setTemplateSet((set) => set.filter((f) => !(f in patch)));
    setStep("edit");
    setFailure(null);
  };
  const pickGame = (game: string) => {
    const follows = !titleTyped && !templateSet.includes("title") && game;
    edit(follows ? { game, title: defaultTitle(nameOf(options.games, game), place.name) } : { game });
  };
  const pickStart = (startAt: number) => {
    const follows = !seasonPicked && !templateSet.includes("seasonId") && !Number.isNaN(startAt);
    edit(follows ? { startAt, seasonId: seasonAt(ctx.seasons, startAt) } : { startAt });
  };
  const pickTemplate = (id: string) => {
    const next = withTemplate(draft, templateSet, rows.find((t) => t.id === id) ?? null, ctx, titleTyped);
    setDraft(next.draft);
    setTemplateSet(next.set);
    setStep("edit");
    setFailure(null);
  };
  // What the review line named, exactly: the change, or the create.
  const send = event && change && update ? () => change.onApply(event, update) : input ? () => onCreate(input) : null;
  const create = () => {
    // The review may have sat open past the start; say so rather than send it.
    const at = Date.now();
    if ((event ? changeProblems(event, draft, at, seasonList) : draftProblems(draft, at, ctx.seasons)).length > 0) return setStep("edit");
    if (!send) return;
    setStep("creating");
    setFailure(null);
    send().then(onClose, (e: unknown) => {
      setFailure(e instanceof Error ? e.message : `Could not ${event ? "change" : "make"} it. Please try again in a moment.`);
      setStep(e instanceof LostAnswerError ? "lost" : "review");
    });
  };
  const presetOptions = (field: "lobbyPreset" | "tablesPreset") =>
    unread.has(field) ? (
      <NativeSelectOption value="">Not read; left as it is</NativeSelectOption>
    ) : (
      <>
        <NativeSelectOption value="">Pick a rules preset</NativeSelectOption>
        {options.presets.map((p) => (
          <NativeSelectOption key={p.slug} value={p.slug}>
            {optionLabel(p, offered.presets)}
          </NativeSelectOption>
        ))}
      </>
    );

  return (
    <form
      data-form={event ? "change-event" : "schedule-event"}
      // The form says its own problems (`draftProblems`); no browser bubbles.
      noValidate
      className="flex flex-col gap-3 rounded-[10px] border border-border bg-panel-2 p-3.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (step === "edit" && problems.length === 0) setStep("review");
      }}
    >
      {/* Nothing in the form can be used while the create is in flight. */}
      <fieldset disabled={step === "creating" || step === "lost"} className="contents">
      <div className="grid gap-3 sm:grid-cols-2">
        {!event && (
        <Field id="schedule-template" label="Start from a template">
          <NativeSelect
            id="schedule-template"
            value={draft.templateId ?? ""}
            disabled={rows.length === 0}
            onChange={(e) => pickTemplate(e.target.value)}
          >
            <NativeSelectOption value="">No template</NativeSelectOption>
            {rows.map((t) => (
              <NativeSelectOption key={t.id} value={t.id}>
                {t.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {note && <span className="text-xs text-text-3">{note}</span>}
        </Field>
        )}
        <Field id="schedule-game" label="Game">
          <NativeSelect id="schedule-game" value={draft.game} onChange={(e) => pickGame(e.target.value)}>
            <NativeSelectOption value="">Pick a game</NativeSelectOption>
            {options.games.map((g) => (
              <NativeSelectOption key={g.slug} value={g.slug}>
                {optionLabel(g, offered.games)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field id="schedule-start" label="Starts">
          <Input
            id="schedule-start"
            type="datetime-local"
            value={Number.isNaN(draft.startAt) ? "" : localInputValue(draft.startAt)}
            onChange={(e) => pickStart(fromLocalInput(e.target.value))}
          />
        </Field>
        <Field id="schedule-minutes" label="Runs for (minutes)">
          <Input
            id="schedule-minutes"
            type="number"
            min={1}
            step={1}
            value={Number.isNaN(draft.minutes) ? "" : draft.minutes}
            onChange={(e) => edit({ minutes: e.target.valueAsNumber })}
          />
        </Field>
        <Field id="schedule-season" label="Season">
          <NativeSelect
            id="schedule-season"
            value={draft.seasonId ?? ""}
            onChange={(e) => {
              setSeasonPicked(true);
              edit({ seasonId: e.target.value || null });
            }}
          >
            {seasonList.map((s) => (
              <NativeSelectOption key={s.id} value={s.id}>
                {s.title}
              </NativeSelectOption>
            ))}
            <NativeSelectOption value="">No season (not shown under Next up)</NativeSelectOption>
          </NativeSelect>
        </Field>
        <Field id="schedule-title" label="Title">
          <Input
            id="schedule-title"
            value={draft.title}
            onChange={(e) => {
              setTitleTyped(true);
              edit({ title: e.target.value });
            }}
          />
        </Field>
        <Field id="schedule-lobby" label="Rules in the lobby">
          <NativeSelect
            id="schedule-lobby"
            value={draft.lobbyPreset}
            disabled={unread.has("lobbyPreset")}
            onChange={(e) => edit({ lobbyPreset: e.target.value })}
          >
            {presetOptions("lobbyPreset")}
          </NativeSelect>
        </Field>
        <Field id="schedule-tables" label="Rules at the tables">
          <NativeSelect
            id="schedule-tables"
            value={draft.tablesPreset}
            disabled={unread.has("tablesPreset")}
            onChange={(e) => edit({ tablesPreset: e.target.value })}
          >
            {presetOptions("tablesPreset")}
          </NativeSelect>
        </Field>
      </div>

      {step === "edit" && problems.length > 0 && <p className="text-xs font-semibold text-text-3">{problems.join(" ")}</p>}
      {step !== "edit" && (
        <p data-review className="rounded-[10px] border border-border bg-card px-3 py-2.5 text-[13px] font-semibold">
          {event && update
            ? changeReviewLine(event, update, { options, seasons: seasonList })
            : input && reviewLine(input, { options, seasons: ctx.seasons, username, template })}
        </p>
      )}
      {failure && <p className="text-[13px] font-semibold text-failure">{failure}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {step === "edit" ? (
          <Button type="submit" size="sm" disabled={problems.length > 0}>
            Review
          </Button>
        ) : step !== "lost" ? (
          <>
            <Button type="button" size="sm" disabled={step === "creating" || !send} onClick={create}>
              {event ? (step === "creating" ? "Applying…" : "Apply") : step === "creating" ? "Creating…" : "Create"}
            </Button>
            <Button type="button" variant="subtle" size="sm" onClick={() => setStep("edit")}>
              Back
            </Button>
          </>
        ) : null}
        {step !== "lost" && (
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {/* Changing, "Cancel" would read as cancelling the event. */}
            {event ? "Close" : "Cancel"}
          </Button>
        )}
        <span className="text-xs text-text-3">
          {event
            ? "Only what you change is sent. The admin console edits everything else."
            : `Made here at ${place.name}. The admin console edits everything else.`}
        </span>
      </div>
      </fieldset>
      {/* Outside the fieldset: after a lost answer, closing is the one way on. */}
      {step === "lost" && (
        <div>
          <Button type="button" variant="subtle" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </form>
  );
}

/** A plain line in a dashed box with its buttons (a read failed, or the
 *  event cannot be changed). */
function FormNote({ children, actions }: { children: React.ReactNode; actions: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-[10px] border border-dashed border-border-strong bg-panel-2 px-3.5 py-3">
      <p className="text-[13px] text-muted-foreground">{children}</p>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}

/** The event a row's Change or Cancel works on, once read: "Reading the
 *  event…", a plain line with "Try again" if the read failed, or why it cannot
 *  be changed (gone, cancelled, started); else `children` with it. */
function WithEvent({
  scheduling,
  eventId,
  close,
  now,
  children,
}: {
  scheduling: Scheduling;
  eventId: string;
  close: React.ReactNode;
  now: number;
  children: (event: EventSnapshot) => React.ReactNode;
}) {
  const asked = scheduling.eventRead?.eventId === eventId ? scheduling.eventRead : null;
  const read = asked?.load ?? null;
  if (!read || read.state === "loading") {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">Reading the event…</p>
        {close}
      </div>
    );
  }
  if (read.state === "error") {
    return (
      <FormNote
        actions={
          <>
            <Button type="button" variant="subtle" size="sm" onClick={() => scheduling.askEvent(eventId)}>
              Try again
            </Button>
            {close}
          </>
        }
      >
        Could not read the event, so it cannot be changed yet.
      </FormNote>
    );
  }
  // Checked as of when it was asked for, never the ticking clock: a form
  // already open (sending, or saying its answer was lost) stays open past the start.
  const problem = read.data ? snapshotProblem(read.data, asked?.askedAt ?? now) : "This event is gone. Next up is read again when the page is.";
  if (problem || !read.data) return <FormNote actions={close}>{problem}</FormNote>;
  return <>{children(read.data)}</>;
}

/** The form: "Reading…" until its games and presets are read and the page's
 *  own reads its defaults come from (the place's games and seasons) have
 *  answered, a plain line with "Try again" if the games and presets failed,
 *  then the form itself. "Cancel" closes it from any of these. A games or
 *  seasons read that failed leaves those defaults out (no pinned game, no season).
 *  `changing` (unit 6c): the id of the event it changes, read first (see
 *  {@link WithEvent}); then "Close" closes it ("Cancel" would read as
 *  cancelling the event). */
export function ScheduleEventForm({
  place,
  scheduling,
  games,
  seasons,
  templates,
  onClose,
  changing,
  now,
  step,
}: {
  place: PlaceData;
  scheduling: Scheduling;
  games: Load<PlaceGames | null>;
  seasons: Load<PlaceSeasonsData | null>;
  templates: StaffGroup<TemplateRow>;
  onClose: () => void;
  changing?: string;
  now?: number;
  step?: FormStep;
}) {
  const options = scheduling.options;
  const cancel = (
    <Button type="button" variant="ghost" size="sm" onClick={onClose}>
      {changing ? "Close" : "Cancel"}
    </Button>
  );
  if (!options || options.state === "loading" || games.state === "loading" || seasons.state === "loading") {
    const waiting = !options || options.state === "loading" ? "Reading the games and rules…" : "Reading this place's games and seasons…";
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">{waiting}</p>
        {cancel}
      </div>
    );
  }
  if (options.state === "error") {
    return (
      <FormNote
        actions={
          <>
            <Button type="button" variant="subtle" size="sm" onClick={scheduling.askOptions}>
              Try again
            </Button>
            {cancel}
          </>
        }
      >
        Could not read the games and rules, so nothing can be {changing ? "changed" : "scheduled"} yet.
      </FormNote>
    );
  }
  const form = (change?: ChangeMode) => (
    <DraftForm
      place={place}
      options={options.data}
      games={games.state === "ready" ? games.data : null}
      seasons={seasons.state === "ready" ? seasons.data : null}
      templates={templates}
      username={scheduling.username}
      onCreate={scheduling.create}
      change={change}
      onClose={onClose}
      now={now}
      step={step}
    />
  );
  if (!changing) return form();
  return (
    <WithEvent scheduling={scheduling} eventId={changing} close={cancel} now={now ?? Date.now()}>
      {(event) => form({ event, onApply: scheduling.change })}
    </WithEvent>
  );
}

/** Where a cancel's review stands: shown, sending, or sent with its answer
 *  lost (no "Cancel it" again). */
export type CancelStep = "review" | "sending" | "lost";

/** "Cancel" on a coming event (unit 6c): the event read, then one line of
 *  what cancelling does (and what its undo does), then "Cancel it" (or
 *  "Back"). "Cancel it" is held within a minute of the start, when the
 *  backend would end it instead. `now` and `step` are for the proof. */
export function CancelEventReview({
  scheduling,
  eventId,
  onClose,
  now: fixedNow,
  step: initialStep = "review",
}: {
  scheduling: Scheduling;
  eventId: string;
  onClose: () => void;
  now?: number;
  step?: CancelStep;
}) {
  const [step, setStep] = React.useState<CancelStep>(initialStep);
  const [failure, setFailure] = React.useState<string | null>(null);
  const now = fixedNow ?? Date.now();
  const back = (
    <Button type="button" variant="ghost" size="sm" onClick={onClose}>
      Back
    </Button>
  );
  return (
    <WithEvent scheduling={scheduling} eventId={eventId} close={back} now={now}>
      {(event) => {
        const tooClose = rowWriteOf(event.startAt, now) !== "open";
        const send = () => {
          setStep("sending");
          setFailure(null);
          scheduling.cancel(event).then(onClose, (e: unknown) => {
            setFailure(e instanceof Error ? e.message : "Could not cancel it. Please try again in a moment.");
            setStep(e instanceof LostAnswerError ? "lost" : "review");
          });
        };
        return (
          <div data-form="cancel-event" className="flex flex-col gap-3 rounded-[10px] border border-border bg-panel-2 p-3.5">
            <p data-review className="rounded-[10px] border border-border bg-card px-3 py-2.5 text-[13px] font-semibold">
              {cancelReviewLine(event, { now })}
            </p>
            {failure && <p className="text-[13px] font-semibold text-failure">{failure}</p>}
            <div className="flex flex-wrap items-center gap-2">
              {step === "lost" ? (
                <Button type="button" variant="subtle" size="sm" onClick={onClose}>
                  Close
                </Button>
              ) : (
                <>
                  <Button type="button" size="sm" disabled={step === "sending" || tooClose} onClick={send}>
                    {step === "sending" ? "Cancelling…" : "Cancel it"}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" disabled={step === "sending"} onClick={onClose}>
                    Back
                  </Button>
                  {tooClose && <span className="text-xs text-text-3">{TOO_CLOSE_LINE}</span>}
                </>
              )}
            </div>
          </div>
        );
      }}
    </WithEvent>
  );
}
